import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { KeycloakService } from '../services/keycloak.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly keycloakService = inject(KeycloakService);
  // Flag to track if a request has already been retried to prevent infinite loops
  private static readonly RETRY_HEADER = 'X-Auth-Retry';

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Skip 401 handling if this request has already been retried
    if (request.headers.has(AuthInterceptor.RETRY_HEADER)) {
      return next.handle(request);
    }

    // Check if request body is FormData - don't set Content-Type for FormData
    // Browser will automatically set Content-Type with boundary for FormData
    const isFormData = request.body instanceof FormData;

    // Skip token refresh for translation files and account API to prevent loops
    const url = request.url.toLowerCase();
    const isTranslationFile =
      url.includes('/assets/i18n/') || url.endsWith('.json');
    const isAccountApi = url.includes('/protocol/openid-connect/account');
    const isTokenApi = url.includes('/protocol/openid-connect/token');

    if (isTranslationFile || isAccountApi || isTokenApi) {
      // For these requests, just add auth header if available, but don't trigger token refresh
      return from(this.addAuthHeaderWithoutRefresh(request, isFormData)).pipe(
        switchMap((authReq) => next.handle(authReq)),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
    }

    return from(this.addAuthHeader(request, isFormData)).pipe(
      switchMap((authReq) => next.handle(authReq)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Try refresh via Keycloak (only if not already retried)
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Attach Keycloak token to request headers without triggering refresh
   * Used for translation files and account API to prevent loops
   */
  private async addAuthHeaderWithoutRefresh(
    request: HttpRequest<any>,
    isFormData: boolean = false,
  ): Promise<HttpRequest<any>> {
    // Get current token without refreshing
    const token = this.keycloakService.getToken();

    if (!token) {
      return request;
    }

    // For FormData, don't set Content-Type - let browser handle it with boundary
    if (isFormData) {
      return request.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
        // Don't set Content-Type for FormData - browser will set it with boundary
      });
    }

    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * Attach Keycloak token to request headers
   * Only refreshes token if it's actually expired
   */
  private async addAuthHeader(
    request: HttpRequest<any>,
    isFormData: boolean = false,
  ): Promise<HttpRequest<any>> {
    let token: string | null = null;

    // Get token from KeycloakService
    if (
      this.keycloakService.isReady() &&
      this.keycloakService.isAuthenticated()
    ) {
      // Only refresh if token is expired, otherwise use current token
      token = await this.ensureValidToken();
    }

    if (!token) {
      return request;
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    const activeTenant = this.resolveActiveTenantId();
    if (activeTenant) {
      if (!request.headers.has('tenant-id')) {
        headers['tenant-id'] = activeTenant;
      }
      if (!request.headers.has('visit-tenant-id')) {
        headers['visit-tenant-id'] = activeTenant;
      }
      if (!request.headers.has('X-Tenant-Id')) {
        headers['X-Tenant-Id'] = activeTenant;
      }
    }

    // For FormData, don't set Content-Type - let browser handle it with boundary
    if (isFormData) {
      return request.clone({
        setHeaders: headers,
        // Don't set Content-Type for FormData - browser will set it with boundary
      });
    }

    return request.clone({
      setHeaders: headers,
    });
  }

  private resolveActiveTenantId(): string | null {
    try {
      const override = globalThis.localStorage?.getItem('oms_active_tenant_id');
      if (override && override.trim() !== '') {
        return override.trim();
      }
    } catch {
      // ignore storage errors
    }
    const parsed = this.keycloakService.getTokenParsed() as
      | { tenant_id?: string | number }
      | undefined;
    if (parsed?.tenant_id !== undefined && parsed?.tenant_id !== null && parsed?.tenant_id !== '') {
      return String(parsed.tenant_id);
    }
    return null;
  }

  // Track if token refresh is in progress to prevent concurrent refreshes
  private isRefreshing = false;

  /**
   * Ensure token is valid, refresh ONLY if expired
   * Never triggers login unexpectedly
   * Only refreshes when token is actually expired and refresh token is valid
   */
  private async ensureValidToken(): Promise<string | null> {
    if (
      !this.keycloakService.isReady() ||
      !this.keycloakService.isAuthenticated()
    ) {
      return null;
    }

    // If refresh is already in progress, return current token
    if (this.isRefreshing) {
      return this.keycloakService.getToken() || null;
    }

    // Only refresh if token is actually expired
    if (this.keycloakService.isTokenExpired()) {
      // Token is expired, try to refresh using refresh token
      if (this.keycloakService.hasValidRefreshToken()) {
        this.isRefreshing = true;
        try {
          const token = await this.keycloakService.updateToken(0); // Force refresh
          return token;
        } finally {
          this.isRefreshing = false;
        }
      } else {
        console.warn(
          '[AuthInterceptor] Token expired and no valid refresh token',
        );
        return null;
      }
    }

    // Token is still valid, return current token without refreshing
    return this.keycloakService.getToken() || null;
  }

  /**
   * Handle 401 errors by attempting token refresh
   * Prevents infinite loops by marking retried requests
   * Never triggers login unexpectedly
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return from(this.tryRefreshToken()).pipe(
      switchMap((newToken) => {
        if (!newToken) {
          // Token refresh failed, but don't trigger login
          // Let the application handle the 401 error
          return throwError(() => new Error('Authentication failed'));
        }

        // Retry request with new token and mark it as retried to prevent loop
        const newRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${newToken}`,
            [AuthInterceptor.RETRY_HEADER]: 'true',
          },
        });

        // Retry the request - if it fails again, it won't trigger another refresh
        return next.handle(newRequest).pipe(
          catchError((retryError: HttpErrorResponse) => {
            // If retry also fails with 401, don't try again (prevent infinite loop)
            // Let the application handle the error
            return throwError(() => retryError);
          }),
        );
      }),
      catchError((error) => {
        // Don't trigger logout/login here
        // Let the application decide what to do
        return throwError(() => error);
      }),
    );
  }

  /**
   * Try to refresh token ONLY if it's expired
   * Uses refresh token automatically if available
   * Returns new token or null if refresh failed
   */
  private async tryRefreshToken(): Promise<string | null> {
    if (
      !this.keycloakService.isReady() ||
      !this.keycloakService.isAuthenticated()
    ) {
      return null;
    }

    // If refresh is already in progress, return current token
    if (this.isRefreshing) {
      return this.keycloakService.getToken() || null;
    }

    // Only refresh if token is actually expired
    if (this.keycloakService.isTokenExpired()) {
      // Token is expired, check if we have a valid refresh token
      if (!this.keycloakService.hasValidRefreshToken()) {
        console.warn(
          '[AuthInterceptor] Token expired and no valid refresh token available',
        );
        return null;
      }

      // Force refresh using refresh token
      this.isRefreshing = true;
      try {
        return await this.keycloakService.updateToken(0); // Force refresh
      } finally {
        this.isRefreshing = false;
      }
    }

    // Token is still valid, return current token without refreshing
    return this.keycloakService.getToken() || null;
  }
}
